// app/javascript/src/pages/Admin/PaymentTerms/List/List.tsx
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import {
  AdminListCard,
  AdminListTable,
  ConfirmDeleteModal,
  PageTabs,
  saveErrorReason,
  useBatchSelection,
  withFailureReasons,
} from "@components/CatalogIndex";
import type { BatchFailure } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { usePaymentTermsList, usePaymentTermMutations } from "../api";
import { pricingModelLabel } from "../types";
import {
  buildPaymentTermBatchActions,
  type PaymentTermTab,
} from "./batchActions";
import type { PaymentTerm } from "../types";

const PAGE_SIZE = 25;

const checkCell = (on: boolean) =>
  on ? <FontAwesomeIcon icon={faCheck} /> : "";

const List = (): JSX.Element => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<PaymentTermTab>("active");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const sel = useBatchSelection([tab]);
  const { archive, restore } = usePaymentTermMutations();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const { rows, pageMeta, isLoading, isFetching, refetch } =
    usePaymentTermsList({
      page: { number: pageNumber, size: pageSize },
      filter: { active: tab === "active" },
    });

  const selected = useMemo(
    () => rows.filter((r) => sel.selectedIds.includes(r.id)),
    [rows, sel.selectedIds]
  );

  const columns: ColumnDef<PaymentTerm>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => row.original.name,
    },
    {
      accessorKey: "pricingModel",
      header: "Pricing",
      cell: ({ row }) => pricingModelLabel(row.original.pricingModel),
    },
    {
      accessorKey: "rounding",
      header: "Rounding",
      cell: ({ row }) => row.original.rounding,
    },
    {
      accessorKey: "includeExpenseInRevenue",
      header: "T&E Revenue",
      cell: ({ row }) => checkCell(row.original.includeExpenseInRevenue),
    },
    {
      accessorKey: "includeExpenseInCost",
      header: "T&E Cost",
      cell: ({ row }) => checkCell(row.original.includeExpenseInCost),
    },
    {
      accessorKey: "includeMaterials",
      header: "Product",
      cell: ({ row }) => checkCell(row.original.includeMaterials),
    },
    {
      accessorKey: "default",
      header: "Default?",
      cell: ({ row }) => checkCell(row.original.default),
    },
  ];

  const runOnSelected = async (
    fn: (pt: PaymentTerm) => Promise<unknown>,
    successText: (n: number) => string
  ) => {
    const targets = [...selected];
    const failures: BatchFailure[] = [];
    for (const pt of targets) {
      try {
        await fn(pt);
      } catch (err) {
        failures.push({ label: pt.name, reason: saveErrorReason(err) });
      }
    }
    setToast(
      failures.length === 0
        ? { variant: "success", text: successText(targets.length) }
        : {
            variant: "danger",
            text: withFailureReasons(
              `Failed on ${failures.length} of ${targets.length} payment term(s).`,
              failures
            ),
          }
    );
    sel.clear();
    refetch();
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    if (tab === "active") {
      await runOnSelected(
        (pt) => archive(pt.id),
        (n) => `${n} payment term(s) archived.`
      );
    } else {
      await runOnSelected(
        (pt) => restore(pt.id, pt.name),
        (n) => `${n} payment term(s) restored.`
      );
    }
  };

  const batchActions = buildPaymentTermBatchActions(
    tab,
    sel.selectedIds.length,
    {
      onDelete: () => setConfirmOpen(true),
      onRestore: () => setConfirmOpen(true),
    }
  );

  if (isLoading) return <ScopeStackSpinner />;

  return (
    <>
      <AdminListCard
        title="Payment Terms"
        onNew={() => navigate("new")}
        newButtonLabel="+ Add A Payment Term"
        batchActions={batchActions}
        selectedCount={sel.selectedIds.length}
      >
        <PageTabs
          activeKey={tab}
          onSelect={(key) => {
            setTab(key as PaymentTermTab);
            setPageNumber(1);
            sel.clear();
          }}
          tabs={[
            {
              key: "active",
              label: "Active",
              count: tab === "active" ? pageMeta.recordCount : undefined,
            },
            {
              key: "archived",
              label: "Archived",
              count: tab === "archived" ? pageMeta.recordCount : undefined,
            },
          ]}
        />
        <AdminListTable
          columns={columns}
          data={rows}
          isLoading={isFetching}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          onEdit={(id) => navigate(`${id}/edit`)}
          emptyMessage="No payment terms found."
          paginationEnabled
          pageSizeEnabled
          totalPages={pageMeta.pageCount}
          totalRows={pageMeta.recordCount}
          currentPage={pageNumber}
          setCurrentPage={(p) => {
            sel.clear();
            setPageNumber(p);
          }}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            sel.clear();
            setPageSize(size);
            setPageNumber(1);
          }}
        />
      </AdminListCard>

      <ConfirmDeleteModal
        open={confirmOpen}
        title={
          tab === "active" ? "Archive payment terms?" : "Restore payment terms?"
        }
        message={`Are you sure you want to ${
          tab === "active" ? "archive" : "restore"
        } ${sel.selectedIds.length} payment term(s)?`}
        confirmLabel={tab === "active" ? "Archive" : "Restore"}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default List;
